import { useState, useEffect, useMemo, Suspense } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { Container } from '@carouselgg/nexus/container'
import { Section } from '@carouselgg/nexus/section'
import { Text } from '@carouselgg/nexus/text'
import { Spinner } from '@carouselgg/nexus/spinner'
import { Callout } from '@carouselgg/nexus/callout'
import { SEO } from '@carouselgg/nexus/seo'
import { FilterBar, FilterOptions, FilterToggle } from '@carouselgg/nexus/filter-bar'
import { SpotlightPanel } from '@carouselgg/nexus/spotlight-panel'
import { Carousel } from '@carouselgg/nexus/carousel'
import { FillMeter } from '@carouselgg/nexus/fill-meter'
import { CompetitionTable, CompetitionRow } from '@carouselgg/nexus/competition-table'
import type { CompetitionColumn } from '@carouselgg/nexus/competition-table'
import { Countdown } from '@carouselgg/nexus/countdown'
import { useAuthStore } from '@carouselgg/auth/store'
import { ActionLink, ActionButton } from '../components/ActionLink'
import { DiscoveryHero, HeroFigure, DiscoveryCTA } from '../components/DiscoveryHero'
import { getGamesWithOpenSeasons, discoverSeasons, discoverUpcomingSeasons, getTrustStanding } from '../api/compete'
import type { GameItem, DiscoverSeason, TrustStanding } from '../api/compete'
import { getOrganizerContact } from '../api/organizations'
import type { OrganizerContact } from '../api/organizations'
import {
  formatDate,
  formatStart,
  formatPrize,
  closesLabel,
  eligibilityLabel,
  seasonHref,
  sortByClosing,
  pickSpotlights,
  freeAgentRoom,
  entrantUnit,
  readResumeSignUp,
  resumeSignUpHref,
  countClosingThisWeek,
  countBlocked,
} from './compete/labels'
import type { RegisterAction } from './compete/labels'
import { OrgCompetitionRegistration } from '../remotes'
import { RegisterChoiceModal } from './compete/RegisterChoiceModal'
import { FreeAgentRoomNote } from './compete/FreeAgentRoomNote'
import { TrustRequirement } from './compete/TrustRequirement'
import { TrustHelpModal } from './compete/TrustHelpModal'
import { TrustScoreChip } from '../components/TrustScoreChip'
import styles from './CompetePage.module.css'

function OrgMark({ season }: { season: DiscoverSeason }) {
  if (season.org_logo_url) {
    return <img src={season.org_logo_url} alt="" className={styles.markImg} />
  }
  return <span>{season.org_name.charAt(0)}</span>
}

// Game is dropped at mid widths; the subtitle already names the kind, and the
// game is one tap away. Below 768px every column comes back, labelled.
const OPEN_COLUMNS: CompetitionColumn[] = [
  { label: 'Game', width: 'minmax(0, 1fr)', collapsible: true },
  { label: 'Entrants', width: '176px' },
  { label: 'Eligibility', width: 'minmax(0, 1.2fr)' },
  { label: 'Prize', width: '88px' },
  { label: 'Closes', width: '110px', align: 'end' },
]

function SeasonRow({ season, trustScore }: { season: DiscoverSeason; trustScore: number | null }) {
  const closes = closesLabel(season.registration_closes_at)
  const eligibility = eligibilityLabel(season, trustScore)
  const isTournament = season.competition_type === 'tournament'
  // "Entrants" left a free agent guessing whether it meant players or teams.
  const unit = entrantUnit(season, season.registered_teams)

  return (
    <CompetitionRow
      to={seasonHref(season)}
      muted={eligibility.blocked}
      mark={<OrgMark season={season} />}
      name={season.name}
      subtitle={`${season.org_name} · ${isTournament ? 'Tournament' : 'League'}`}
      cells={[
        { content: season.game_name },
        {
          content: season.max_teams ? (
            <FillMeter
              value={season.registered_teams}
              max={season.max_teams}
              size="sm"
              label={`${season.registered_teams} of ${season.max_teams}`}
              aria-label={`${season.registered_teams} of ${season.max_teams} ${entrantUnit(season, season.max_teams)} registered`}
            />
          ) : `${season.registered_teams} ${unit}`,
        },
        { content: eligibility.text },
        { content: season.prize_pool > 0 ? formatPrize(season.prize_pool) : '—' },
        { content: closes.text, strong: closes.urgent },
      ]}
    />
  )
}

export function CompetePage() {
  const [games, setGames] = useState<GameItem[]>([])
  const [selectedGame, setSelectedGame] = useState<string | null>(null)
  const [seasons, setSeasons] = useState<DiscoverSeason[]>([])
  const [upcoming, setUpcoming] = useState<DiscoverSeason[]>([])
  const [cashOnly, setCashOnly] = useState(false)
  // The competition whose "Register to Compete" chooser is open.
  const [registerFor, setRegisterFor] = useState<DiscoverSeason | null>(null)
  // The sign-up running in place, once a route is picked.
  const [signingUp, setSigningUp] = useState<{ season: DiscoverSeason; action: RegisterAction } | null>(null)
  // Who runs the competition being signed up for, so a refused sign-up can say
  // who to ask. Keyed by slug so a stale answer never labels another org's event.
  const [organizer, setOrganizer] = useState<{ slug: string; contact: OrganizerContact } | null>(null)
  const signingUpOrgSlug = signingUp?.season.org_slug
  // Bumped after a sign-up so the entrant counts on the page catch up.
  const [reloadKey, setReloadKey] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [gamesLoading, setGamesLoading] = useState(true)

  // Deep-link support: /compete?type=tournament pre-selects the filter, so a
  // "Find a Tournament" CTA can land here already scoped to tournaments.
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const initialType = searchParams.get('type')
  const [typeFilter, setTypeFilter] = useState<string | null>(
    initialType === 'tournament' || initialType === 'league' ? initialType : 'all',
  )

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  // Each competition's Trust minimum is judged on the player's score for its
  // game (or overall, where per-game eligibility is off — the API decides).
  // Keyed by game; loaded only for games with a minimum on the page.
  const [standings, setStandings] = useState<Map<string, TrustStanding>>(new Map())
  const [trustHelpFor, setTrustHelpFor] = useState<DiscoverSeason | null>(null)
  const scoreFor = (season: Pick<DiscoverSeason, 'game_id'>) => standings.get(season.game_id)?.score ?? null
  const isAuthLoading = useAuthStore((s) => s.isLoading)

  // Back from login with a sign-up requested: open it once the competition is
  // on the page, then drop the request from the URL so a refresh doesn't
  // reopen it. A competition that has closed meanwhile is simply not there.
  const resume = readResumeSignUp(searchParams)
  const resumeSeasonId = resume?.seasonId
  const resumeAction = resume?.action
  useEffect(() => {
    if (!resumeSeasonId || !resumeAction || loading || isAuthLoading || !isAuthenticated) return
    const season = seasons.find((s) => s.id === resumeSeasonId)
    if (season) setSigningUp({ season, action: resumeAction })
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('register')
      next.delete('as')
      return next
    }, { replace: true })
  }, [resumeSeasonId, resumeAction, loading, isAuthLoading, isAuthenticated, seasons, setSearchParams])

  useEffect(() => {
    if (!signingUpOrgSlug) return
    let cancelled = false
    getOrganizerContact(signingUpOrgSlug)
      .then((contact) => { if (!cancelled) setOrganizer({ slug: signingUpOrgSlug, contact }) })
      // The contact line is optional — the sign-up never waits on or fails with it.
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [signingUpOrgSlug])

  // Straight to the free-agent sign-up, skipping the team-or-alone chooser —
  // through login first for a visitor who isn't signed in.
  const joinPool = (season: DiscoverSeason) => {
    if (isAuthenticated) setSigningUp({ season, action: 'free-agent' })
    else navigate(`/login?redirect=${encodeURIComponent(resumeSignUpHref(season, 'free-agent'))}`)
  }

  useEffect(() => {
    let cancelled = false
    setGamesLoading(true)
    getGamesWithOpenSeasons()
      .then((g) => {
        if (cancelled) return
        setGames(g ?? [])
        setGamesLoading(false)
      })
      .catch(() => { if (!cancelled) setGamesLoading(false) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      discoverSeasons(selectedGame ?? undefined),
      discoverUpcomingSeasons(selectedGame ?? undefined),
    ])
      .then(([openRes, upcomingRes]) => {
        if (cancelled) return
        setSeasons(openRes.seasons ?? [])
        setTotal(openRes.total)
        setUpcoming(upcomingRes.seasons ?? [])
        setLoading(false)
      })
      .catch(() => {
        if (!cancelled) {
          setSeasons([]); setUpcoming([]); setTotal(0); setLoading(false)
        }
      })
    return () => { cancelled = true }
  }, [selectedGame, reloadKey])

  const trustGameIds = useMemo(
    () => [...new Set(seasons.filter((s) => (s.min_trust_score ?? 0) > 0).map((s) => s.game_id))].sort().join(','),
    [seasons],
  )
  useEffect(() => {
    if (!isAuthenticated || !trustGameIds) { setStandings(new Map()); return }
    let cancelled = false
    Promise.allSettled(trustGameIds.split(',').map((id) => getTrustStanding(id))).then((results) => {
      if (cancelled) return
      const next = new Map<string, TrustStanding>()
      for (const r of results) if (r.status === 'fulfilled') next.set(r.value.game_id, r.value)
      setStandings(next)
    })
    return () => { cancelled = true }
  }, [isAuthenticated, trustGameIds, reloadKey])

  const hasTournaments = seasons.some((s) => s.competition_type === 'tournament')
  const typeFiltered = !typeFilter || typeFilter === 'all'
    ? seasons
    : seasons.filter((s) => s.competition_type === typeFilter)
  const hasCashSeasons = typeFiltered.some((s) => s.prize_pool > 0)
  const visible = cashOnly ? typeFiltered.filter((s) => s.prize_pool > 0) : typeFiltered

  const sorted = useMemo(() => sortByClosing(visible), [visible])
  const { spotlights, rest, featured } = pickSpotlights(sorted)

  const closingThisWeek = countClosingThisWeek(seasons)
  const totalPrize = seasons.reduce((sum, s) => sum + s.prize_pool, 0)
  const blockedCount = countBlocked(seasons, scoreFor)

  const typeOptions = [
    { value: 'all', label: 'All' },
    { value: 'league', label: 'Leagues' },
    ...(hasTournaments || typeFilter === 'tournament'
      ? [{ value: 'tournament', label: 'Tournaments' }]
      : []),
  ]

  const gameOptions = games.map((g) => ({
    value: g.id,
    label: g.name,
    ...(g.logo_url ? { icon: <img src={g.logo_url} alt="" /> } : {}),
  }))


  const hasActiveFilters =
    Boolean(selectedGame) || (typeFilter !== null && typeFilter !== 'all') || cashOnly

  // With a single open competition the spotlight is the whole list: the table
  // and the "opening soon" rail both render nothing, so the page would drop
  // straight from one panel into the closing sections with no explanation.
  const isOnlyOne = !loading && sorted.length === 1 && upcoming.length === 0

  // One spotlight card. The carousel pages through one per featured
  // competition (or shows the soonest-closing one alone).
  const renderSpotlight = (spotlight: DiscoverSeason) => {
    const spotlightRoom = freeAgentRoom(spotlight)
    return (
    <SpotlightPanel
      {...(spotlight.org_primary_color ? { brandColor: spotlight.org_primary_color } : {})}
      logo={<OrgMark season={spotlight} />}
      eyebrow={
        <>
          <Text size="sm" weight="semibold" className={styles.block}>{spotlight.org_name}</Text>
          <Text size="xs" muted>
            {spotlight.division_name}
            {spotlight.competition_type === 'tournament' ? ' · Tournament' : ' · League'}
          </Text>
        </>
      }
      title={spotlight.name}
      // The Trust minimum has its own block in the rail; here, the rank range.
      meta={[spotlight.game_name, eligibilityLabel({ ...spotlight, min_trust_score: null }, null).text]}
      aside={
        <>
          {/* A season with no registration deadline rendered as the
              heading "Registration closes" above a bare em dash,
              which reads as broken data. Fall back to when it
              actually starts, which is the fact that is present. */}
          {spotlight.registration_closes_at ? (
            <div>
              <Text size="xs" muted className={styles.block}>Registration closes in</Text>
              {/* Live, to the second. When it hits zero the list is
                  re-read, so a closed competition leaves the spotlight
                  instead of offering a sign-up that will be refused. */}
              <Countdown
                to={spotlight.registration_closes_at}
                size="lg"
                expiredText="Closed"
                onComplete={() => setReloadKey((k) => k + 1)}
                className={styles.railCountdown}
              />
              <Text size="xs" muted className={styles.block}>
                {new Date(spotlight.registration_closes_at).toLocaleString(undefined, {
                  weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                })}
              </Text>
            </div>
          ) : spotlight.starts_at ? (
            <div>
              <Text size="xs" muted className={styles.block}>Starts</Text>
              <span className={styles.railFigure}>{formatStart(spotlight.starts_at, spotlight.competition_type)}</span>
              <Text size="xs" muted className={styles.block}>Registration open until it fills</Text>
            </div>
          ) : (
            <div>
              <Text size="xs" muted className={styles.block}>Registration</Text>
              <span className={styles.railFigure}>Open</span>
              <Text size="xs" muted className={styles.block}>Start date to be confirmed</Text>
            </div>
          )}
          {/* Always shown: "no minimum" is an answer too. */}
          <TrustRequirement
            min={spotlight.min_trust_score}
            gameName={spotlight.game_name}
            standing={standings.get(spotlight.game_id) ?? null}
            signedIn={isAuthenticated}
            signInHref={`/login?redirect=${encodeURIComponent('/compete')}`}
            onHelp={() => setTrustHelpFor(spotlight)}
          />
          {spotlight.prize_pool > 0 && (
            <div>
              <Text size="xs" muted className={styles.block}>Prize pool</Text>
              <span className={styles.railFigure}>{formatPrize(spotlight.prize_pool)}</span>
            </div>
          )}
        </>
      }
      actions={
        <>
          <ActionButton onClick={() => setRegisterFor(spotlight)}>
            Register to Compete
          </ActionButton>
          <ActionLink to={seasonHref(spotlight)} variant="secondary">
            View competition
          </ActionLink>
        </>
      }
    >
      {/* `max_teams` is nullable. Uncapped, there is no meter to
          draw — but the panel still needs a body, so state the
          count plainly rather than leaving a hole under the title. */}
      {spotlight.max_teams ? (
        <FillMeter
          value={spotlight.registered_teams}
          max={spotlight.max_teams}
          label={`${spotlight.registered_teams} of ${spotlight.max_teams} ${entrantUnit(spotlight, spotlight.max_teams)} registered`}
          remainingLabel={`${Math.max(0, spotlight.max_teams - spotlight.registered_teams)} ${spotlight.team_size > 1 ? 'team places' : 'places'} left`}
        />
      ) : (
        <Text size="sm" muted>
          {spotlight.registered_teams === 0
            ? `No ${entrantUnit(spotlight, 0)} yet — be the first in.`
            : `${spotlight.registered_teams} ${entrantUnit(spotlight, spotlight.registered_teams)} registered so far. No cap.`}
        </Text>
      )}
      {/* The meter counts teams; a player without one needs to
          know where they would fit. */}
      {spotlightRoom && (
        <FreeAgentRoomNote room={spotlightRoom} onJoinPool={() => joinPool(spotlight)} />
      )}
    </SpotlightPanel>
    )
  }

  return (
    <div className={styles.page}>
      <SEO
        title="Compete"
        description="Open esports leagues and tournaments taking registrations now. Pick your game, register your team, or sign up as a free agent."
      />

      <Container size="lg">
        <DiscoveryHero
          eyebrow="Compete"
          title={<>Find your<br />competition.</>}
          subtext="Find a league or tournament at your level. Bring a roster, or sign up alone as a free agent."
          aside={
            <>
              <HeroFigure value={String(total)} label="open now" />
              {closingThisWeek > 0 && (
                <HeroFigure value={String(closingThisWeek)} label="closing this week" />
              )}
              {totalPrize > 0 && (
                <HeroFigure value={formatPrize(totalPrize)} label="in prizes" />
              )}
            </>
          }
        />

        <FilterBar
          primary={
            <FilterOptions
              label="Competition type"
              variant="segment"
              options={typeOptions}
              value={typeFilter}
              onChange={setTypeFilter}
            />
          }
          actions={
            <>
              {hasCashSeasons && (
                <FilterToggle pressed={cashOnly} onChange={setCashOnly}>Cash prizes</FilterToggle>
              )}
              <TrustScoreChip />
            </>
          }
        >
          {gamesLoading ? (
            <Spinner size="sm" />
          ) : gameOptions.length > 0 && (
            <FilterOptions
              label="Game"
              allLabel="All games"
              options={gameOptions}
              value={selectedGame}
              onChange={setSelectedGame}
            />
          )}
        </FilterBar>
      </Container>

      {loading ? (
        <Section spacing="lg">
          <Container size="lg">
            <div className={styles.loadingWrap}><Spinner /></div>
          </Container>
        </Section>
      ) : sorted.length === 0 ? (
        <Section spacing="lg">
          <Container size="lg">
            <Callout variant="info">
              {cashOnly
                ? 'No open competitions with cash prizes right now. Turn off the filter to see them all.'
                : typeFilter === 'tournament'
                  ? 'No open tournaments right now. Check back soon, or switch to All.'
                  : `No open competitions found${selectedGame ? ' for this game' : ''}. Check back soon, or browse league pages directly.`}
            </Callout>
          </Container>
        </Section>
      ) : (
        <>
          {spotlights.length > 0 && (
            <Section spacing="md">
              <Container size="lg">
                <Carousel
                  key={spotlights.map((c) => c.id).join()}
                  label={featured ? 'Featured competitions' : 'Closing soonest'}
                  header={<p className={`${styles.railLabel} ${styles.railLabelInBar}`}>{featured ? 'Featured' : 'Closing soonest'}</p>}
                >
                  {spotlights.map((spotlight) => <div key={spotlight.id}>{renderSpotlight(spotlight)}</div>)}
                </Carousel>

                {isOnlyOne && (
                  <p className={styles.footnote}>
                    That&rsquo;s the only competition open right now.{' '}
                    {hasActiveFilters ? (
                      <>Clear the filters to widen the search, or <Link to="/create-org?type=league">start one of your own</Link>.</>
                    ) : (
                      <><Link to="/create-org?type=league">Start one of your own</Link>.</>
                    )}
                  </p>
                )}
              </Container>
            </Section>
          )}

          {rest.length > 0 && (
            <Section spacing="lg">
              <Container size="lg">
                <div className={styles.listHead}>
                  <Text as="h2" size="xl" weight="bold" className={styles.listTitle}>Open now</Text>
                  <Text size="sm" muted>{rest.length} more</Text>
                  <span className={styles.spacer} />
                  <Text size="sm" muted className={styles.sortNote}>Sorted by closing date</Text>
                </div>

                <CompetitionTable columns={OPEN_COLUMNS} aria-label="Open now">
                  {rest.map((season) => (
                    <SeasonRow key={season.id} season={season} trustScore={scoreFor(season)} />
                  ))}
                </CompetitionTable>

                {blockedCount > 0 && (
                  <p className={styles.footnote}>
                    {blockedCount === 1
                      ? 'One competition sits above your Trust Score.'
                      : `${blockedCount} competitions sit above your Trust Score.`}{' '}
                    <Link to="/settings/connections">Link another game account</Link>
                  </p>
                )}
              </Container>
            </Section>
          )}

          {/* Free agency was a full-width band with its own display heading,
              which on a page showing one competition made the pitch taller
              than the content. It is a secondary action for someone already
              looking at the list, so it reads as a footnote to the list. */}
          <Container size="lg">
            <p className={styles.footnote}>
              No team? Put your name in the free-agent pool on any competition above — captains
              see your rank, your role and your verified history.{' '}
              <Link to="/settings/connections">Set up your profile</Link>
            </p>
          </Container>
        </>
      )}

      {!loading && upcoming.length > 0 && (
        <Section spacing="lg">
          <Container size="lg">
            <Text as="h2" size="xl" weight="bold" className={styles.listTitle}>Opening soon</Text>
            <div className={styles.rail}>
              {upcoming.map((season) => (
                <div key={season.id} className={styles.railItem}>
                  <span className={styles.railTick} aria-hidden="true" />
                  <span className={styles.railDate}>{formatDate(season.registration_opens_at)}</span>
                  <span className={styles.railName}>{season.name}</span>
                  <span className={styles.railOrg}>{season.org_name} · {season.division_name}</span>
                </div>
              ))}
            </div>
          </Container>
        </Section>
      )}

      <Container size="lg">
        <DiscoveryCTA
          title="Run your own."
          subtext="Leagues or tournaments, same editor, same screening. Set the rank range and the trust floor, and Carousel checks every sign-up against them."
          to="/create-org?type=league"
          label="Create a league"
          secondaryTo="/for/leagues"
          secondaryLabel="How leagues work"
        />
      </Container>

      {trustHelpFor && standings.get(trustHelpFor.game_id) && (
        <TrustHelpModal
          open
          onClose={() => setTrustHelpFor(null)}
          standing={standings.get(trustHelpFor.game_id)!}
          min={trustHelpFor.min_trust_score ?? 0}
          gameName={trustHelpFor.game_name}
        />
      )}

      <RegisterChoiceModal
        season={registerFor}
        onClose={() => setRegisterFor(null)}
        onChoose={(_, action) => {
          if (registerFor) setSigningUp({ season: registerFor, action })
          setRegisterFor(null)
        }}
      />

      {signingUp && (
        <Suspense fallback={null}>
          <OrgCompetitionRegistration
            orgId={signingUp.season.organization_id}
            season={{
              id: signingUp.season.id,
              name: signingUp.season.name,
              gameId: signingUp.season.game_id,
              gameName: signingUp.season.game_name,
              gameLogoUrl: signingUp.season.game_logo_url,
              divisionName: signingUp.season.division_name,
              venueMode: signingUp.season.venue_mode,
              registrationClosesAt: signingUp.season.registration_closes_at,
            }}
            mode={signingUp.action}
            organizer={organizer?.slug === signingUp.season.org_slug ? organizer.contact : undefined}
            onClose={() => setSigningUp(null)}
            onRegistered={() => setReloadKey((k) => k + 1)}
            onFreeAgentJoined={() => setReloadKey((k) => k + 1)}
          />
        </Suspense>
      )}
    </div>
  )
}
